import { Checkbox as AntCheckbox, DatePicker, InputNumber, Segmented, Switch } from 'antd';
import dayjs from 'dayjs';
import { AlertTriangle } from 'lucide-react';

import { Select } from '../../../../components/ui';
import type { FieldControlProps } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { useDichCaiDat } from '../../hooks/useDichCaiDat';
import type {
  BienHieuLuc,
  KenhThongBao,
  LoiTruong,
  MucDoThongBao,
  QuyTacCanhBao,
  SettingDefinition,
  TuyenThongBao,
} from '../../types';
import { laTiLe } from '../../nghiep-vu/don-vi';
import { loiMayChuTheoQuyTac } from '../../nghiep-vu/quy-tac-canh-bao';
import { BangQuyTacCanhBao } from './BangQuyTacCanhBao';
import { ODaiTinCay } from './ODaiTinCay';

const KENH: KenhThongBao[] = ['EMAIL', 'CHAT', 'PORTAL'];
const MUC_DO: MucDoThongBao[] = ['INFO', 'WARNING', 'CRITICAL'];

/** Ô kiểu `notify_route`: kênh, người nhận, mức tối thiểu (ADR-0059 mục 6a.2). */
function OTuyenThongBao({
  giaTri,
  onDoi,
  chiDoc,
  p,
}: {
  giaTri: TuyenThongBao;
  onDoi: (v: TuyenThongBao) => void;
  chiDoc: boolean;
  p: FieldControlProps;
}) {
  const { t } = useT();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div className="min-w-0 md:col-span-7">
          <span className="mb-1 block text-xs text-content-muted">{t('caiDat.tuyen.nguoiNhan')}</span>
          <Select<string[]>
            {...p}
            mode="tags"
            className="w-full"
            disabled={chiDoc}
            value={giaTri.recipients}
            tokenSeparators={[',', ' ', ';']}
            placeholder={t('caiDat.tuyen.nguoiNhanViDu')}
            open={false}
            suffixIcon={null}
            onChange={(v) => onDoi({ ...giaTri, recipients: v.map((s) => s.trim()).filter(Boolean) })}
          />
        </div>
        <div className="min-w-0 md:col-span-5">
          <span className="mb-1 block text-xs text-content-muted">{t('caiDat.tuyen.mucToiThieu')}</span>
          <Select<MucDoThongBao>
            className="w-full"
            disabled={chiDoc}
            aria-label={t('caiDat.tuyen.mucToiThieu')}
            value={giaTri.min_severity}
            options={MUC_DO.map((m) => ({ value: m, label: t(`caiDat.mucDo.${m}` as KhoaDich) }))}
            onChange={(v) => onDoi({ ...giaTri, min_severity: v })}
          />
        </div>
      </div>
      <fieldset className="min-w-0">
        <legend className="mb-1 text-xs text-content-muted">{t('caiDat.tuyen.kenh')}</legend>
        <AntCheckbox.Group
          disabled={chiDoc}
          value={giaTri.channels}
          options={KENH.map((k) => ({ value: k, label: t(`caiDat.kenh.${k}` as KhoaDich) }))}
          onChange={(v) => onDoi({ ...giaTri, channels: v as KenhThongBao[] })}
        />
      </fieldset>
      {giaTri.recipients.length === 0 && (
        // ADR-0059 mục 6a.2: không có người nhận thì vẫn ghi vào cổng, không lặng lẽ bỏ.
        <p className="flex items-start gap-1.5 text-xs text-warning-700">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          {t('caiDat.tuyen.chuaCoNguoiNhan')}
        </p>
      )}
    </div>
  );
}

/**
 * Ô nhập của một tham số, chọn theo `type` của danh mục.
 *
 * Không kẹp giá trị vào biên (`InputNumber` không nhận `min`/`max`): kẹp im lặng
 * là sửa hộ người dùng mà không nói, còn để họ gõ rồi báo "Từ 1 đến 50" ngay
 * dưới ô thì họ biết vì sao.
 */
export function DieuKhien({
  def,
  giaTri,
  onDoi,
  chiDoc,
  p,
  ipHienTai,
  bien = null,
  loiMayChu = [],
  daLuu,
}: {
  def: SettingDefinition;
  giaTri: unknown;
  onDoi: (v: unknown) => void;
  chiDoc: boolean;
  p: FieldControlProps;
  ipHienTai: string | null;
  /** Biên hiệu lực của dòng — ô cần biên từng trường (`alert_rule_list`) đọc ở đây. */
  bien?: BienHieuLuc | null;
  /** Lỗi theo trường của lần ghi vừa rồi. */
  loiMayChu?: readonly LoiTruong[];
  /** Giá trị đã lưu của dòng — ô danh sách IP so với nó để gõ về như cũ không thành thay đổi. */
  daLuu?: unknown;
}) {
  const { t } = useT();
  const d = useDichCaiDat();

  switch (def.type) {
    case 'boolean':
      return (
        <span className="flex items-center gap-2">
          <Switch
            id={p.id}
            aria-describedby={p['aria-describedby']}
            aria-invalid={p['aria-invalid']}
            checked={giaTri === true}
            disabled={chiDoc}
            onChange={onDoi}
          />
          <span className="text-sm text-content-secondary">
            {t(giaTri === true ? 'caiDat.bat' : 'caiDat.tat')}
          </span>
        </span>
      );

    case 'integer':
    case 'number': {
      const tiLe = laTiLe(def);
      const so = typeof giaTri === 'number' ? (tiLe ? Math.round(giaTri * 1000) / 10 : giaTri) : null;
      return (
        <span className="flex items-center gap-2">
          <InputNumber
            {...p}
            className="w-40"
            status={p['aria-invalid'] ? 'error' : undefined}
            value={so}
            disabled={chiDoc}
            step={tiLe ? 1 : def.type === 'number' ? 0.01 : 1}
            onChange={(v) => {
              if (v === null || v === undefined) onDoi(undefined);
              else onDoi(tiLe ? Number(v) / 100 : Number(v));
            }}
          />
          <span className="text-sm text-content-muted">{tiLe ? '%' : d.donVi(def.unit)}</span>
        </span>
      );
    }

    case 'enum': {
      const options = (def.values ?? []).map((v) => ({ value: v, label: d.giaTriEnum(def.key, v) }));
      return (def.values ?? []).length <= 3 ? (
        <Segmented<string>
          id={p.id}
          className="segmented-xuong-dong max-w-full"
          aria-describedby={p['aria-describedby']}
          options={options}
          value={String(giaTri)}
          disabled={chiDoc}
          onChange={onDoi}
        />
      ) : (
        <Select<string> {...p} className="w-64" value={String(giaTri)} options={options} disabled={chiDoc} onChange={onDoi} />
      );
    }

    case 'timestamp':
      return (
        <DatePicker
          id={p.id}
          aria-describedby={p['aria-describedby']}
          className="w-60"
          showTime={{ format: 'HH:mm' }}
          format="DD/MM/YYYY HH:mm"
          disabled={chiDoc}
          value={typeof giaTri === 'string' ? dayjs(giaTri) : null}
          placeholder={t('caiDat.khongDat')}
          disabledDate={(x) => x.isBefore(dayjs(), 'day')}
          onChange={(v) => onDoi(v ? v.toISOString() : null)}
        />
      );

    case 'cidr_list':
      // Kiểu này chỉ còn dải tin cậy (blocklist tenant là bảng riêng, ADR-0090 mục 3): ô nhập theo dòng (ADR-0098)
      return (
        <ODaiTinCay
          giaTri={giaTri}
          onDoi={onDoi}
          chiDoc={chiDoc}
          p={p}
          ipHienTai={ipHienTai}
          max={bien?.max_items ?? def.bounds?.max_items}
          daLuu={daLuu}
        />
      );

    case 'notify_route':
      return (
        <OTuyenThongBao giaTri={giaTri as TuyenThongBao} onDoi={onDoi} chiDoc={chiDoc} p={p} />
      );

    case 'alert_rule_list':
      return (
        <BangQuyTacCanhBao
          id={p.id}
          ten={d.nhan(def.key)}
          giaTri={Array.isArray(giaTri) ? (giaTri as QuyTacCanhBao[]) : []}
          onDoi={onDoi}
          chiDoc={chiDoc}
          bien={bien ?? def.bounds ?? null}
          loiMayChu={loiMayChuTheoQuyTac(loiMayChu, def.key)}
        />
      );

    case 'integer_list':
    case 'string_list': {
      const laSo = def.type === 'integer_list';
      const ds = Array.isArray(giaTri) ? (giaTri as unknown[]).map(String) : [];
      return (
        <Select<string[]>
          {...p}
          mode="tags"
          className="w-full max-w-lg"
          disabled={chiDoc}
          value={ds}
          open={false}
          suffixIcon={null}
          tokenSeparators={[',', ' ', ';']}
          placeholder={t(laSo ? 'caiDat.danhSachSoViDu' : 'caiDat.danhSachChuoiViDu')}
          onChange={(v) =>
            onDoi(
              laSo
                ? v.map((s) => (/^-?\d+$/.test(s.trim()) ? Number(s) : Number.NaN))
                : v.map((s) => s.trim()),
            )
          }
        />
      );
    }

    default:
      return <span className="text-sm text-content-muted">{d.giaTri(def, giaTri)}</span>;
  }
}
